/**
 * OrderItemSelector — grid-based item picker for reprint/refund drawers.
 *
 * Owns the item row state internally and reports valid selections
 * (item selected + quantity > 0) to the parent via `onValidItemsChange`.
 */
import { tokens, Input, Button } from "@blurb/codex-react";
import { createStyles } from "@mantine/core";
import { RpiAlert, IconV3 } from "@rpi/openapi-core";
import React, { forwardRef } from "react";
import { Body, Label } from "../../components/Typography";
import { RpaDropdown } from "../../components/inputFields/RpaDropdownInput";

const useStyles = createStyles({
  itemsWrapper: {
    flex: "auto",
    display: "grid",
    gridTemplateColumns: "3fr 1fr",
    gap: tokens.spacing[2],
    alignItems: "center",
    paddingBottom: tokens.spacing[2],
  },
  itemsDivider: {
    gridColumn: "span 2",
    borderBottom: `1px solid ${tokens.color.semantic.border.faint}`,
  },
  itemsError: {
    gridColumn: "span 2",
  },
  itemSelecter: {
    gridColumn: "1",
  },
  itemQuantity: {
    gridColumn: "2",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    alignContent: "center",
    justifyItems: "center",
  },
  removeButton: {
    background: "none",
    border: "none",
    padding: 0,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  orderItemLabel: {
    whiteSpace: "normal",
    wordBreak: "break-word" as const,
  },
});

/* ---------- Order item dropdown rendering ---------- */

interface OrderItemSelectItemProps extends React.ComponentPropsWithoutRef<"div"> {
  label: string;
  value: string;
  description?: string;
  "data-selected"?: boolean;
}

/**
 * Custom item renderer for the order item dropdown. Shows the item ID + name
 * on the first line and "(Qty. N)" on the second, with text wrapping.
 */
const OrderItemSelectItem = forwardRef<HTMLDivElement, OrderItemSelectItemProps>(
  ({ label, description, "data-selected": dataSelected, ...others }, ref) => {
    const { classes } = useStyles();
    return (
      <div ref={ref} {...others}>
        <Body weight={dataSelected ? "bold" : "normal"} className={classes.orderItemLabel}>
          {label}
        </Body>
        {description && <Body size="sm">{description}</Body>}
      </div>
    );
  }
);

OrderItemSelectItem.displayName = "OrderItemSelectItem";

/* ---------- Types ---------- */

interface ItemRow {
  orderItemId: number | undefined;
  quantity: string;
}

export interface ValidItem {
  orderItemId: number;
  quantity: number;
}

/** Minimal shape of an order item needed by this component. */
export interface OrderItem {
  id: number;
  openapiProduct?: { name?: string | null } | null;
  customerProduct?: { name?: string | null } | null;
  quantity?: number | null;
}

interface OrderItemSelectorProps {
  orderItems: readonly OrderItem[];
  /** Called whenever the set of valid items changes. */
  onValidItemsChange: (items: ValidItem[]) => void;
  /** When defined, displays a validation error alert. */
  error?: string;
}

/* ---------- Component ---------- */

export function OrderItemSelector({ orderItems, onValidItemsChange, error }: OrderItemSelectorProps) {
  const { classes } = useStyles();
  const [itemRows, setItemRows] = React.useState<ItemRow[]>([]);

  const validItems = React.useRef<ValidItem[]>([]);

  /** Derive valid items from rows and notify the parent when they change. */
  const deriveAndNotify = (rows: ItemRow[]) => {
    const next = rows.flatMap((row) => {
      if (row.orderItemId === undefined) return [];
      const qty = parseInt(row.quantity, 10);
      if (Number.isNaN(qty) || qty <= 0) return [];
      return [{ orderItemId: row.orderItemId, quantity: qty }];
    });

    const changed =
      next.length !== validItems.current.length ||
      next.some(
        (item, i) =>
          item.orderItemId !== validItems.current[i]?.orderItemId || item.quantity !== validItems.current[i]?.quantity
      );

    if (changed) {
      validItems.current = next;
      onValidItemsChange(next);
    }
  };

  const updateRows = (updater: (prev: ItemRow[]) => ItemRow[]) => {
    setItemRows((prev) => {
      const next = updater(prev);
      deriveAndNotify(next);
      return next;
    });
  };

  /** Order items not yet added to the reprint list. */
  const availableOrderItems = orderItems.filter((oi) => !itemRows.some((row) => row.orderItemId === oi.id));

  const orderItemSelectData = (currentId: number | undefined) =>
    orderItems
      .filter((oi) => oi.id === currentId || !itemRows.some((row) => row.orderItemId === oi.id))
      .map((oi) => ({
        value: String(oi.id),
        label: `${oi.id} - ${oi.openapiProduct?.name ?? oi.customerProduct?.name ?? `Item #${oi.id}`}`,
        description: `(Qty. ${oi.quantity ?? 0})`,
      }));

  const handleAddItem = () => {
    updateRows((prev) => [...prev, { orderItemId: undefined, quantity: "" }]);
  };

  const handleRemoveItem = (index: number) => {
    updateRows((prev) => prev.filter((_, i) => i !== index));
  };

  const handleItemChange = (index: number, value: string | null) => {
    updateRows((prev) =>
      prev.map((row, i) => (i === index ? { ...row, orderItemId: value ? Number(value) : undefined } : row))
    );
  };

  const handleQuantityChange = (index: number, rawValue: string) => {
    updateRows((prev) => prev.map((row, i) => (i === index ? { ...row, quantity: rawValue } : row)));
  };

  return (
    <div>
      <div className={classes.itemsWrapper}>
        <Label size="base" weight="semibold">
          Item(s)
        </Label>
        <Label size="base" weight="semibold">
          Qty.
        </Label>
        <div className={classes.itemsDivider} />

        {error && <RpiAlert className={classes.itemsError}>{error}</RpiAlert>}

        {itemRows.flatMap((row, index) => [
          <RpaDropdown
            key={`itemselect-${row.orderItemId}`}
            className={classes.itemSelecter}
            placeholder="Select.."
            data={orderItemSelectData(row.orderItemId)}
            value={row.orderItemId !== undefined ? String(row.orderItemId) : null}
            onChange={(val) => handleItemChange(index, val)}
            itemComponent={OrderItemSelectItem}
          />,
          <div key={`itemquantity-${row.orderItemId}`} className={classes.itemQuantity}>
            <Input
              type="number"
              min={0}
              max={999}
              value={row.quantity}
              disabled={row.orderItemId === undefined}
              onChange={(e) => handleQuantityChange(index, e.target.value)}
              aria-label="Quantity"
            />
            <button className={classes.removeButton} onClick={() => handleRemoveItem(index)} aria-label="Remove item">
              <IconV3 icon="remove" size={24} />
            </button>
          </div>,
        ])}
      </div>

      <Button
        variant="outlined"
        intent="neutral"
        size="small"
        fullWidth
        onClick={handleAddItem}
        disabled={
          (availableOrderItems.length === 0 && itemRows.length > 0) ||
          itemRows.some((row) => row.orderItemId === undefined)
        }
      >
        <IconV3 icon="add" size={16} />
        Add Item
      </Button>
    </div>
  );
}
